import React, { useState } from 'react';
import {
    Alert,
    Button,
    CircularProgress,
    Container,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';

import { Page } from '../../../components';
import HeaderBreadcrumbs from '../../../components/header-breadcrumbs';
import {
    DeletionRequestRow,
    useApproveDeletionRequest,
    useDenyDeletionRequest,
    usePendingDeletionRequests,
} from './deletionRequests.api';

const title = 'POM - Deletion Requests';
const heading = 'Account Deletion Requests';

const contactOf = (row: DeletionRequestRow) => row.employeeId || row.phoneNumber || '—';

export const AccountDeletionRequestsScreen: React.FC = () => {
    const { data: rows, isLoading, isError } = usePendingDeletionRequests();
    const approve = useApproveDeletionRequest();
    const deny = useDenyDeletionRequest();

    // Approval removes the user from active use (a record is kept per our
    // data-retention policy), so it goes through a confirm dialog; deny is
    // non-destructive and applies immediately.
    const [confirming, setConfirming] = useState<DeletionRequestRow | null>(null);

    const onConfirmApprove = () => {
        if (confirming) {
            approve.mutate(confirming.id);
        }
        setConfirming(null);
    };

    return (
        <Page title={title}>
            <Container maxWidth={false}>
                <HeaderBreadcrumbs
                    heading={heading}
                    links={[
                        {
                            name: 'Approve or deny requests from users to delete their account',
                            href: '/account-deletion-requests',
                        },
                    ]}
                    activeLast={false}
                />

                {isLoading && (
                    <Stack alignItems="center" py={6}>
                        <CircularProgress />
                    </Stack>
                )}

                {isError && (
                    <Alert severity="error">
                        Could not load deletion requests. Refresh the page to try again.
                    </Alert>
                )}

                {!isLoading && !isError && (rows?.length ?? 0) === 0 && (
                    <Typography color="text.secondary" py={4}>
                        No pending deletion requests.
                    </Typography>
                )}

                {!isLoading && !isError && (rows?.length ?? 0) > 0 && (
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Name</TableCell>
                                    <TableCell>Contact</TableCell>
                                    <TableCell>Requested</TableCell>
                                    <TableCell align="right">Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {rows!.map((row) => (
                                    <TableRow key={row.id}>
                                        <TableCell>{row.name}</TableCell>
                                        <TableCell>{contactOf(row)}</TableCell>
                                        <TableCell>{new Date(row.requestedAt).toLocaleString()}</TableCell>
                                        <TableCell align="right">
                                            <Stack direction="row" spacing={1} justifyContent="flex-end">
                                                <Button
                                                    variant="outlined"
                                                    disabled={deny.isLoading || approve.isLoading}
                                                    onClick={() => deny.mutate(row.id)}
                                                >
                                                    Deny
                                                </Button>
                                                <Button
                                                    variant="contained"
                                                    color="error"
                                                    disabled={deny.isLoading || approve.isLoading}
                                                    onClick={() => setConfirming(row)}
                                                >
                                                    Approve
                                                </Button>
                                            </Stack>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}

                <Dialog open={confirming !== null} onClose={() => setConfirming(null)}>
                    <DialogTitle>Approve deletion request?</DialogTitle>
                    <DialogContent>
                        <DialogContentText>
                            This removes {confirming?.name ?? 'this user'}&apos;s account from active
                            use, along with their devices and emergency-service registration, and logs
                            them out of the app. A record of the account is kept per our
                            data-retention policy.
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setConfirming(null)}>Cancel</Button>
                        <Button color="error" variant="contained" onClick={onConfirmApprove}>
                            Approve and delete
                        </Button>
                    </DialogActions>
                </Dialog>
            </Container>
        </Page>
    );
};
